import { ISubscription } from "@/services/SubscriptionService";
import * as Style from "../RePaymentSubscriptionStyle";
import UpgradeSubscriptionSelector from "./UpgradeSubscriptionSelector";
import { decimalFormatter } from "@/utils/numberFormatter";
import { TElementsResponse } from "./ElementsTable";
import { Separator, Spinner, SpinnerSize } from "@fluentui/react";
import { TUpgradeMode } from "@/services/UsersSubscriptionService";
import { isFullPriceUpgradeMode } from "./upgradeSubscriptionRules";

type TPaymentInfoSubscriptionProps =
{
    subscriptionsAvailable: ISubscription[];
    // La tarifa elegida vive en el padre (UpgradeSubscription), que es quien paga.
    selectedSubscription?: ISubscription | null;
    onSelectedSubscriptionChange: (subscription: ISubscription) => void;
    elementsTotal: TElementsResponse;
    elementWaterPrice: number;
    elementEarthPrice: number;
    // Modo de mejora que indica el backend: en los de tarifa completa no hay prorrateo ni días restantes.
    mode: TUpgradeMode | null;
    remainingDays: number;
    // Importes en euros: chargePrice del backend (antes de Elements) y precio final tras Elements.
    chargePrice: number;
    finalPrice: number;
    // Aviso de validez según el modo (getUpgradeNotice).
    noticeText?: string | null;
    canPay: boolean;
    isSubmitting: boolean;
    errorMessage?: string;
    onPay: () => void;
};

export const PaymentInfoSubscription = (
{
    subscriptionsAvailable,
    selectedSubscription,
    onSelectedSubscriptionChange,
    elementsTotal,
    elementWaterPrice,
    elementEarthPrice,
    mode,
    remainingDays,
    chargePrice,
    finalPrice,
    noticeText,
    canPay,
    isSubmitting,
    errorMessage,
    onPay,
}: TPaymentInfoSubscriptionProps) =>
{
    return(
        <Style.CardContainer>
            <Style.PaymentCard>
                <Style.PaymentItem>
                    <Style.PaymentLabel></Style.PaymentLabel>
                    <Style.PaymentValue>
                        <UpgradeSubscriptionSelector
                            subscriptions={subscriptionsAvailable ?? []}
                            selectedId={selectedSubscription?.id}
                            disabled={isSubmitting}
                            onChange={onSelectedSubscriptionChange}
                        />
                    </Style.PaymentValue>
                </Style.PaymentItem>
                {mode === "prorated" &&
                (
                    <>
                        <Style.PaymentItem>
                            <Style.PaymentLabel>
                                Precio original {selectedSubscription?.name ?? "N/A"}
                            </Style.PaymentLabel>
                            <Style.PaymentValue>
                                {decimalFormatter.format(selectedSubscription?.price || 0)} €
                            </Style.PaymentValue>
                        </Style.PaymentItem>
                        <Style.PaymentItem>
                            <Style.PaymentLabel>Días restantes de tu suscripción</Style.PaymentLabel>
                            <Style.PaymentValue>{remainingDays}</Style.PaymentValue>
                        </Style.PaymentItem>
                        <Style.PaymentItem>
                            <Style.PaymentLabel>
                                Precio suscripción prorrateado
                            </Style.PaymentLabel>
                            <Style.PaymentValue>
                                {decimalFormatter.format(chargePrice)} €
                            </Style.PaymentValue>
                        </Style.PaymentItem>
                    </>
                )}
                {isFullPriceUpgradeMode(mode) &&
                (
                    <Style.PaymentItem>
                        <Style.PaymentLabel>
                            Precio suscripción {selectedSubscription?.name ?? "N/A"}
                        </Style.PaymentLabel>
                        <Style.PaymentValue>
                            {decimalFormatter.format(chargePrice)} €
                        </Style.PaymentValue>
                    </Style.PaymentItem>
                )}
                <Separator />
                <Style.PaymentItem>
                    <Style.PaymentLabel>Elements Tierra</Style.PaymentLabel>
                    <Style.PaymentValue>
                        {decimalFormatter.format(parseFloat((elementsTotal.totalEarth * elementEarthPrice).toFixed(2)))} €
                    </Style.PaymentValue>
                </Style.PaymentItem>
                <Style.PaymentItem>
                    <Style.PaymentLabel>Elements Agua</Style.PaymentLabel>
                    <Style.PaymentValue>
                        {decimalFormatter.format(parseFloat((elementsTotal.totalWater * elementWaterPrice).toFixed(2)))} €
                    </Style.PaymentValue>
                </Style.PaymentItem>
                <Style.PaymentItem>
                    <Style.PaymentLabel>Elements IA</Style.PaymentLabel>
                    <Style.PaymentValue>
                        {decimalFormatter.format(elementsTotal.totalIA * 0.01)} €
                    </Style.PaymentValue>
                </Style.PaymentItem>
                <Style.PaymentItem>
                    <Style.PaymentLabel>Descuento total por elements</Style.PaymentLabel>
                    <Style.PaymentValue>
                        - {decimalFormatter.format(elementsTotal.totalDiscountByElements)} €
                    </Style.PaymentValue>
                </Style.PaymentItem>
                <Separator />
                <Style.PaymentItem>
                    <Style.PaymentLabel>Precio final</Style.PaymentLabel>
                    <Style.PaymentValue>
                        {decimalFormatter.format(finalPrice)} €
                    </Style.PaymentValue>
                </Style.PaymentItem>
                <Separator />
                <Style.PaymentInfo>
                    {noticeText &&
                    (
                        <p style={{fontWeight: "bold"}}>{noticeText}</p>
                    )}
                    <p>Beneficiario</p>
                    <p>DIAPHANUM VALORES, SV, S.A.U</p>
                    <p>A87637450 Maria de Molina 40, Planta 7 - Madrid 28006</p>
                </Style.PaymentInfo>
                {errorMessage !== undefined &&
                (
                    <Style.FlexContainer>
                        <Style.TextRightAligned>
                            <Style.InfoIconStyled id="upgradeSubscription" />
                            {errorMessage}
                        </Style.TextRightAligned>
                    </Style.FlexContainer>
                )}
                {isSubmitting &&
                (
                    <Style.PayButtonDisabled disabled={true}>
                        <Style.SpinnerContainer>
                            <Spinner size={SpinnerSize.small} className="button-spinner" />
                            <Style.SpinnerText>
                                Pagando...
                            </Style.SpinnerText>
                        </Style.SpinnerContainer>
                    </Style.PayButtonDisabled>
                )}
                {!isSubmitting &&
                (
                    <Style.PayButton
                        disabled={!canPay}
                        style={canPay ? undefined : {opacity: 0.5, cursor: "not-allowed"}}
                        onClick={() =>
                        {
                            if(canPay)
                            {
                                onPay();
                            }
                        }}
                    >
                        Pagar
                    </Style.PayButton>
                )}
            </Style.PaymentCard>
        </Style.CardContainer>
    );
};
